if (!customElements.get('theme-sticky-header')) {
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      this._rafId = null;
      this.lastScrollY = window.scrollY;
      this.scrolledThreshold = 0;
      this._directionAnchor = window.scrollY;
      this._currentDirection = null;
      this._searchOpen = false;

      this.offset = 0;
      this._ctaQuery = window.matchMedia('(min-width: 1024px)');
      this._resizeObserver = new ResizeObserver(() => {
        this._publishHeaderHeight();
        this._publishOffset();
      });
      this._refresh();
      document.addEventListener('shopify:section:load', () => this._refresh(), { signal });
      document.addEventListener('shopify:section:unload', () => this._refresh(), { signal });

      this._ctaObserver = new MutationObserver(() => this._publishOffset());
      this._ctaObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-sticky-cta-top-visible'],
      });
      this._ctaQuery.addEventListener('change', () => this._publishOffset(), { signal });

      this._searchModalOpenUnsub = theme.pubsub.subscribe(theme.pubsub.events.MODAL_OPEN, (data) => {
        if (data?.id !== 'header-search-modal' || !this._isActive() || !this._isSearchDropdown()) return;
        this._searchOpen = true;
        this.header.classList.add('header--search-open');
        this._syncTransparentScheme(true);
      });
      this._searchModalCloseUnsub = theme.pubsub.subscribe(theme.pubsub.events.MODAL_CLOSE, (data) => {
        if (data?.id !== 'header-search-modal' || !this._isActive() || !this._isSearchDropdown()) return;
        this._searchOpen = false;
        this.header.classList.remove('header--search-open');
        this._syncTransparentScheme(window.scrollY > this.scrolledThreshold);
      });

      window.addEventListener('scroll', () => {
        if (this._rafId || !this._isActive()) return;
        this._rafId = requestAnimationFrame(() => {
          this._handleScroll();
          this._publishOffset();
          this._rafId = null;
        });
      }, { passive: true, signal });
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._resizeObserver?.disconnect();
      this._ctaObserver?.disconnect();
      if (this._rafId) cancelAnimationFrame(this._rafId);
      this._searchModalOpenUnsub?.();
      this._searchModalCloseUnsub?.();
    }

    _refresh() {
      this._resizeObserver.disconnect();
      this.header = this.querySelector('.header');
      this.mode = this.header?.dataset.stickyMode ?? 'none';
      if (!this.header) return;

      this._resizeObserver.observe(this);
      this._resizeObserver.observe(this.header);
      this.querySelectorAll('.announcement-bar').forEach((bar) => this._resizeObserver.observe(bar));
      this._publishHeaderHeight();
      this._offsetPublished = false;
      this._publishOffset();
      if (this._isActive()) this._syncTransparentScheme(window.scrollY > this.scrolledThreshold || this._searchOpen);
    }

    _announcementBars({ sticky }) {
      return [...this.querySelectorAll('.announcement-bar')].filter(
        (bar) => bar.classList.contains('announcement-bar--sticky') === sticky
      );
    }

    _computeOffset() {
      if (!this.header) return 0;

      if (!this._isActive()) {
        return Math.max(0, ...this._announcementBars({ sticky: true }).map((bar) => bar.offsetHeight));
      }

      const ctaHidesGroup =
        document.documentElement.hasAttribute('data-sticky-cta-top-visible') && this._ctaQuery.matches;
      if (ctaHidesGroup || this.classList.contains('sticky-group--fully-hidden')) return 0;

      if (this.classList.contains('sticky-group--bar-hidden')) {
        const hiddenHeight = this._announcementBars({ sticky: false }).reduce((sum, bar) => sum + bar.offsetHeight, 0);
        return Math.max(0, this.offsetHeight - hiddenHeight);
      }

      return this.offsetHeight;
    }

    _publishOffset() {
      const offset = this._computeOffset();
      if (offset === this.offset && this._offsetPublished) return;

      this.offset = offset;
      this._offsetPublished = true;

      const rootStyle = document.documentElement.style;
      if (this._isActive()) {
        rootStyle.setProperty('--header-group-bottom', `${offset}px`);
        rootStyle.removeProperty('--sticky-bars-height');
      } else {
        rootStyle.setProperty('--sticky-bars-height', `${offset}px`);
        rootStyle.removeProperty('--header-group-bottom');
      }

      theme.pubsub.publish(theme.pubsub.events.HEADER_OFFSET_CHANGE, { offset });
    }

    _isActive() {
      return !!this.header && this.mode !== 'none';
    }

    _isSearchDropdown() {
      return !!this.header.querySelector('.header-search--dropdown');
    }

    _publishHeaderHeight() {
      if (!this.header) return;
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }

    _handleScroll() {
      if (getComputedStyle(document.documentElement).overflowY === 'hidden') return;

      const currentScrollY = window.scrollY;
      const delta = currentScrollY - this.lastScrollY;
      this.lastScrollY = currentScrollY;

      const isScrolled = currentScrollY > this.scrolledThreshold;
      this.header.classList.toggle('header--scrolled', isScrolled);
      this._syncTransparentScheme(isScrolled || this._searchOpen);

      const barCanHide = this._announcementBars({ sticky: false }).length > 0;
      this.classList.toggle('sticky-group--bar-hidden', isScrolled && barCanHide);

      if (this.mode !== 'hide_on_scroll_down') return;

      if (!isScrolled) {
        this.classList.remove('sticky-group--fully-hidden');
        this._currentDirection = null;
        return;
      }

      const rawDirection = delta > 0 ? 'down' : delta < 0 ? 'up' : null;
      if (!rawDirection) return;

      if (rawDirection !== this._currentDirection) {
        this._directionAnchor = currentScrollY;
        this._currentDirection = rawDirection;
      }

      const accumulated = Math.abs(currentScrollY - this._directionAnchor);
      if (accumulated < 10) return;

      const atBottom = currentScrollY + window.innerHeight >= document.documentElement.scrollHeight - 8;

      if (this._currentDirection === 'down' && currentScrollY > this.offsetHeight) {
        this.classList.add('sticky-group--fully-hidden');
      } else if (this._currentDirection === 'up' && !atBottom) {
        this.classList.remove('sticky-group--fully-hidden');
      }
    }

    _syncTransparentScheme(isScrolled) {
      if (!this.header.classList.contains('header--transparent')) return;
      if (this.header.classList.contains('header--menu-open')) return;

      const targetScheme = isScrolled ? this.header.dataset.colorScheme : this.header.dataset.colorSchemeTransparent;
      if (!targetScheme) return;

      const targetClass = `color-${targetScheme}`;
      const currentClass = Array.from(this.header.classList).find((className) => className.startsWith('color-'));
      if (currentClass === targetClass) return;

      if (currentClass) this.header.classList.remove(currentClass);
      this.header.classList.add(targetClass);
    }
  }

  customElements.define('theme-sticky-header', StickyHeader);
}
